/* eslint-disable no-secrets/no-secrets */
import { MenuCategory } from '../../../header/models/category.model';
import { PAGES_WITH_PAGE_VIEW_AFFECTED_BY_CHECKOUT_FLOW } from '../constants/ga4-analytics.constants';
import { CustomContext, GA4ContextParam, SidebarAnalyticsData } from '../models/configuration.model';
import {
    GA4ContextGroup,
    GA4ContextValues,
    PageSectionValue,
    PageTitleValue,
    PageTypeValue,
    ProductContext,
    StoreStatusValue,
    UserLogguedString,
    UserProfileStatus,
} from '../models/context.model';
import { getPageReferrer } from '../services/ga4-page-referrer.service';
import { Ga4ShopCartDataSelector } from '../store/ga4-analytics.selectors';
import * as GA4DataUtils from '../utils/ga4-data.utils';

import { getStoreId, getUserEmailSha256, getUserId } from './ga4-user.helper';

import { ApplicationSections } from '@core/domain/models/application-section.model';
import { Category } from '@core/domain/models/category.models';
import { PageSection } from '@core/domain/types/page-sections.types';
import { ProductPublicModels } from '@features/products/product';
import { SidebarCustomComponents } from '@features/sidebar';
import { User } from '@features/user';
import { isDefined } from '@frontend/common/utils';
import { routerPublicModels } from '@frontend/libs/duttirouter';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ModalData } from '@ui/dialog/store/modal.state';

export const defaultConfig: GA4ContextGroup = {
    page_location: undefined,
    page_referrer: undefined,
    page_title: undefined,
    im_page_section: undefined,
    im_page_type: undefined,
    im_page_title: undefined,
    im_language: undefined,
    im_platform: undefined,
    im_local_store_id: undefined,
    im_store_status: undefined,
    im_step: undefined,
    im_user_email_sha256: undefined,
    user_id: undefined,
    im_physical_store: undefined,
    im_product_section: undefined,
    im_product_family: undefined,
    im_product_subfamily: undefined,
    im_user_logged: undefined,
    im_user_loyalty: undefined,
    im_user_quick_buy: undefined,
    im_user_profile_status: undefined,
};
export const popUpIncludedAnalytics: string[] = [
    'app-feel-subscription-modal',
    'app-my-account-feel-unsuscription-modal',
    'itx-product-stock',
    'product-modal',
];

// El uso de SidebarPublicModels.sidebarCustomComponents provoca errores en los tests
export const sidebarPageviewAnalytics = [
    'OrderDetailLayoutComponent',
    'OrderReturnRecommendationComponent', // SidebarPublicModels.SidebarPublicModels.sidebarCustomComponents.ORDER_RETURN_RECOM,
    'OutOfStockSidebarComponent', // SidebarPublicModels.SidebarPublicModels.sidebarCustomComponents.OUT_OF_STOCK,
    'ProductAddedToCartModalComponent', // SidebarPublicModels.SidebarPublicModels.sidebarCustomComponents.PRODUCT_ADDED_CART,
    'ProductComingSoonComponent', // SidebarPublicModels.SidebarPublicModels.sidebarCustomComponents.COMING_SOON,
];

export const pageSections: Record<PageSection, PageSectionValue> = {
    Itx404ErrorPage: 'error',
    Itx410ErrorPage: 'error',
    ItxAngularAuthenticationConfirmationPage: 'perfil_usuario',
    ItxAngularAuthenticationPage: 'acceso',
    ItxAngularAuthenticationRecoveryPage: 'acceso',
    ItxAngularAuthenticationRegisterPage: 'acceso',
    ItxAngularCmsRenderPage: 'corporativo',
    ItxAngularContactPage: 'ayuda',
    ItxAngularGiftCardActivationPage: 'catalogo',
    ItxAngularGiftCardBalancePage: 'catalogo',
    ItxAngularGiftCardPage: 'catalogo',
    ItxAngularLookbookPage: 'catalogo',
    ItxAngularMyAccountPage: 'perfil_usuario',
    ItxAngularUserPage: 'perfil_usuario',
    ItxAngularOrderConfirmationPage: 'checkout',
    ItxAngularOrderConfirmationNodePage: 'checkout',
    ItxAngularOrderStatus: 'localizador_pedido',
    ItxAngularShopcartPage: 'checkout',
    ItxAngularStoreDetailLocatorPage: 'localizador_tiendas',
    ItxAngularStoreLocatorPage: 'localizador_tiendas',
    ItxAngularTransactionCancelation: 'ayuda',
    ItxAngularWishlistPage: 'catalogo',
    ItxCategoryPage: 'catalogo',
    ItxCheckoutAuthPage: 'checkout',
    ItxCheckoutPage: 'checkout',
    ItxCheckoutPaymentPage: 'checkout',
    ItxCheckoutRepaymentPage: 'checkout',
    ItxCheckoutShippingDeliveryAddressPage: 'checkout',
    ItxCheckoutShippingDeliveryPage: 'checkout',
    ItxCheckoutShippingDeliverySummaryPage: 'checkout',
    ItxCheckoutShippingDropPointPage: 'checkout',
    ItxCheckoutShippingDropPointSummaryPage: 'checkout',
    ItxCheckoutShippingPage: 'checkout',
    ItxCheckoutShippingStorePage: 'checkout',
    ItxCheckoutShippingStoreSummaryPage: 'checkout',
    ItxCheckoutSummaryPage: 'checkout',
    ItxErrorPage: 'error',
    ItxFastSintPage: 'localizador_tiendas',
    ItxHelpPage: 'ayuda',
    ItxHomeBgPage: 'entrada',
    ItxHomeMenPage: 'entrada',
    ItxHomePage: 'entrada',
    ItxHomeWomenPage: 'entrada',
    ItxMassimoDuttiFeelPage: 'corporativo',
    ItxMyAccountAccessDataPage: 'perfil_usuario',
    ItxMyAccountAddressListPage: 'perfil_usuario',
    ItxMyAccountCardsPage: 'perfil_usuario',
    ItxMyAccountDetailsPage: 'perfil_usuario',
    ItxMyAccountFeelActivationPage: 'perfil_usuario',
    ItxMyAccountFeelBenefitsDetailPage: 'perfil_usuario',
    ItxMyAccountFeelBenefitsPage: 'perfil_usuario',
    ItxMyAccountFeelBirthdayGiftPage: 'perfil_usuario',
    ItxMyAccountFeelCollectionsPage: 'perfil_usuario',
    ItxMyAccountFeelExperiencesPage: 'perfil_usuario',
    ItxMyAccountFeelGiftingPage: 'perfil_usuario',
    ItxMyAccountFeelHistoryPage: 'perfil_usuario',
    ItxMyAccountFeelHowItWorksPage: 'perfil_usuario',
    ItxMyAccountFeelPage: 'perfil_usuario',
    ItxMyAccountFeelPaperlessPage: 'perfil_usuario',
    ItxMyAccountFeelPersonalTailorPage: 'perfil_usuario',
    ItxMyAccountFeelPromotionsPage: 'perfil_usuario',
    ItxMyAccountFeelStyleAdvisorPage: 'perfil_usuario',
    ItxMyAccountFeelWhatsappPage: 'perfil_usuario',
    ItxMyAccountMenuPage: 'perfil_usuario',
    ItxMyAccountNewsletterPage: 'perfil_usuario',
    ItxMyAccountOrderDetailUpdateShippingAddress: 'perfil_usuario',
    ItxMyAccountOrdersPage: 'perfil_usuario',
    ItxMyAccountPage: 'perfil_usuario',
    ItxMyAccountReturnsPage: 'perfil_usuario',
    ItxMyAccountSponsorshipPage: 'perfil_usuario',
    ItxNewsletterPage: 'corporativo',
    ItxOrderConfirmationPage: 'checkout',
    ItxProductPage: 'catalogo',
    ItxWorldWidePage: 'entrada',
};

export const getPageType: Record<PageSection, PageTypeValue> = {
    /**
     * TODO: Completar resto de page types
     * Si no se puede usar el parámetro page de la store de redux será necesario customizar este tipo de pageview
     * Casuística para las páginas que llegan de cms => ItxAngularCmsRenderPage (diferenciar valor del page type por el slug)
     */
    Itx404ErrorPage: '404',
    Itx410ErrorPage: '410',
    ItxAngularAuthenticationConfirmationPage: 'acceso',
    ItxAngularAuthenticationPage: 'acceso',
    ItxAngularAuthenticationRecoveryPage: 'acceso',
    ItxAngularAuthenticationRegisterPage: 'acceso',
    ItxAngularContactPage: 'contacto',
    ItxAngularGiftCardActivationPage: 'gift_card',
    ItxAngularGiftCardBalancePage: 'gift_card',
    ItxAngularGiftCardPage: 'gift_card',
    ItxAngularLookbookPage: 'lookbook',
    ItxAngularOrderConfirmationPage: 'confirmacion',
    ItxAngularOrderConfirmationNodePage: 'confirmacion',
    ItxAngularOrderStatus: 'localizar',
    ItxAngularShopcartPage: 'cesta',
    ItxAngularStoreDetailLocatorPage: 'ficha_tienda',
    ItxAngularStoreLocatorPage: 'localizar',
    ItxAngularTransactionCancelation: 'transaction_cancelation',
    ItxAngularWishlistPage: 'wishlist',
    ItxBagCollectionCreatorsPage: 'women_bags',
    ItxBagCollectionProductsPage: 'women_bags',
    ItxBagCollectionSliderCreatorsPage: 'women_bags',
    ItxBagCollectionSliderProductsPage: 'women_bags',
    ItxCategoryPage: 'parrilla',
    ItxCheckoutAuthPage: 'acceso',
    ItxCheckoutPage: 'envio',
    ItxCheckoutPaymentPage: 'pago',
    ItxCheckoutRepaymentPage: 'repago',
    ItxCheckoutShippingDeliveryAddressPage: 'envio',
    ItxCheckoutShippingDeliveryPage: 'envio',
    ItxCheckoutShippingDeliverySummaryPage: 'envio',
    ItxCheckoutShippingDropPointPage: 'envio',
    ItxCheckoutShippingDropPointSummaryPage: 'envio',
    ItxCheckoutShippingPage: 'envio',
    ItxCheckoutShippingStorePage: 'envio',
    ItxCheckoutShippingStoreSummaryPage: 'envio',
    ItxCheckoutSummaryPage: 'resumen_compra',
    ItxFastSintPage: 'click_collect',
    ItxHelpPage: 'preguntas_frecuentes',
    ItxHomeBgPage: 'home',
    ItxHomeMenPage: 'home',
    ItxHomePage: 'home',
    ItxHomeWomenPage: 'home',
    ItxMassimoDuttiFeelPage: 'fidelizacion',
    ItxMyAccountAccessDataPage: 'datos_acceso',
    ItxMyAccountAddressListPage: 'libro_direcciones',
    ItxMyAccountCardsPage: 'compra_rapida',
    ItxMyAccountDetailsPage: 'detalles_cuenta',
    ItxMyAccountFeelActivationPage: 'fidelizacion',
    ItxMyAccountFeelBenefitsDetailPage: 'fidelizacion',
    ItxMyAccountFeelBenefitsPage: 'fidelizacion',
    ItxMyAccountFeelBirthdayGiftPage: 'fidelizacion',
    ItxMyAccountFeelCollectionsPage: 'fidelizacion',
    ItxMyAccountFeelExperiencesPage: 'fidelizacion',
    ItxMyAccountFeelGiftingPage: 'fidelizacion',
    ItxMyAccountFeelHistoryPage: 'fidelizacion',
    ItxMyAccountFeelHowItWorksPage: 'fidelizacion',
    ItxMyAccountFeelPage: 'fidelizacion',
    ItxMyAccountFeelPaperlessPage: 'fidelizacion',
    ItxMyAccountFeelPersonalTailorPage: 'fidelizacion',
    ItxMyAccountFeelPromotionsPage: 'fidelizacion',
    ItxMyAccountFeelStyleAdvisorPage: 'fidelizacion',
    ItxMyAccountFeelWhatsappPage: 'fidelizacion',
    ItxMyAccountMenuPage: 'menu_usuario',
    ItxMyAccountNewsletterPage: 'newsletter',
    ItxMyAccountOrderDetailUpdateShippingAddress: 'pedidos_realizados',
    ItxMyAccountOrdersPage: 'pedidos_realizados',
    ItxMyAccountPage: 'menu_usuario',
    ItxMyAccountReturnsPage: 'devolucion',
    ItxMyAccountSponsorshipPage: 'sponsorship',
    ItxNewsletterPage: 'newsletter',
    ItxOrderConfirmationPage: 'confirmacion',
    ItxProductPage: 'ficha_producto',
    ItxRunwayPage: 'parrilla',
    ItxWorldWidePage: 'worldwide',
};

export const getPageTitleForCheckoutShipping: Record<PageSection, PageTitleValue> = {
    ItxCheckoutShippingDeliveryPage: 'direccion/lista',
    ItxCheckoutShippingDeliveryAddressPage: 'direccion/formulario',
    ItxCheckoutShippingDeliverySummaryPage: 'resumen/domicilio',
    ItxCheckoutShippingDropPointPage: 'localizador_droppoint/localizar/lista',
    ItxCheckoutShippingStorePage: 'localizador_tiendas/localizar/lista',
    ItxCheckoutShippingStoreSummaryPage: 'resumen/recogida_tienda',
    ItxCheckoutShippingDropPointSummaryPage: 'resumen/recogida_droppoint',
};

// Las urls de páginas de CMS pueden cambiar
export const getPageTypeForCmsPage = (url: string): PageTypeValue | undefined => {
    let lastSegment: string = url.split('/').pop();
    const indexOfFirstParam: number = lastSegment.indexOf('?');
    if (indexOfFirstParam !== -1) {
        lastSegment = lastSegment.slice(0, indexOfFirstParam);
    }
    switch (lastSegment) {
        case 'help':
            return 'preguntas_frecuentes';
        case 'history':
            return 'empresa';
        case 'member-get-member':
        case 'intro-member':
            return 'landing_membergetmember';
        case 'thenow':
            return 'thenow';
        default:
            return undefined;
    }
};

// Las urls de páginas de Collections CMS pueden cambiar
export const getPageTypeForCollectionPage = (url: string): PageTypeValue | undefined => {
    if (url.includes('/stories/')) {
        return 'stories';
    }
    return undefined;
};

export const getPageTypeForCategoryPage = (url: string): PageTypeValue => {
    let lastSegment: string = url.split('/').pop();
    lastSegment = lastSegment.slice(0, lastSegment.indexOf('-'));
    switch (lastSegment) {
        case 'campana':
            return 'campana';
        default:
            return 'parrilla';
    }
};

export const isValidModal = (modal: ModalData<any>): boolean => {
    return modal?.name && popUpIncludedAnalytics.includes(modal.name);
};

export const isValidSidebarComponent = (sidebarComponent: SidebarCustomComponents) => {
    return sidebarComponent && sidebarPageviewAnalytics.includes(sidebarComponent);
};

export const getPageTypeForProductPage = (type: ProductPublicModels.PublicProductType): PageTypeValue => {
    return type === 'BundleBean' ? 'ficha_bundle' : 'ficha_producto';
};

export const getPageTypeForProductModal = (data: ProductContext): PageTypeValue => {
    if (data.isCart) {
        return 'cesta';
    }
    if (data.relatedType) {
        return 'vista_rapida_producto_cross';
    }
    if (data.bundleReference) {
        return 'vista_rapida_producto_bundle';
    }
    return 'vista_rapida_producto';
};

// biome-ignore lint/correctness/noUnusedVariables: <explanation>
export const getCustomPageTypeForModals = (page: string, modalData: ModalData<any>): PageTypeValue | undefined => {
    switch (modalData.name) {
        case 'itx-product-stock':
            return 'disponibilidad_en_tienda';
        case 'product-modal': {
            const data: ProductContext = modalData.data as ProductContext;
            return getPageTypeForProductModal(data);
        }
        case 'app-feel-subscription-modal':
            return 'acceso';
        case 'app-my-account-feel-unsuscription-modal':
            return 'fidelizacion';
        default:
            return undefined;
    }
};

export const getCustomPageTypeForSidebars = (
    page: PageSection,
    sidebarComponent: SidebarCustomComponents
): PageTypeValue | undefined => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (sidebarComponent) {
        // El uso de SidebarPublicModels.sidebarCustomComponents provoca errores en los tests
        case 'SizeGuideComponent':
            return 'guia_tallas';
        case 'OutOfStockSidebarComponent':
        case 'ProductAddedToCartModalComponent':
        case 'ProductComingSoonComponent':
        case 'OrderDetailLayoutComponent':
        case 'OrderReturnRecommendationComponent':
            return getPageType[page];
        case 'SastreRecommenderComponent':
            return 'recomendador_tallas';
    }
};

export const getCustomPageTypeByEventFrom = (eventFrom: EventFrom): PageTypeValue | undefined => {
    switch (eventFrom) {
        case EventFrom.search:
        case EventFrom.searchAdapter:
        case EventFrom.searchNoResults:
        case EventFrom.searchAdapterNoResults:
            return 'buscador';
        case EventFrom.miniWishList:
            return 'miniwishlist';
        case EventFrom.miniCart:
            return 'minicesta';
        case EventFrom.storeAndLanguageList:
            return 'worldwide';
        case EventFrom.login:
        case EventFrom.loginFacebook:
            return 'login';
        case EventFrom.retrievePassEmail:
            return 'acceso';
        case EventFrom.orderStatusList:
        case EventFrom.orderStatusNotFound:
            return 'localizar';
        case EventFrom.returnsOrderPage:
            return 'metodo_devolucion';
        case EventFrom.returnsOrderListPage:
        case EventFrom.returnsOrderDetailPage:
        case EventFrom.returnsOrderConfirmationPage:
            return 'devolucion';
        case EventFrom.returnsStoreFreeChanges:
            return 'perfil_usuario/pedidos_realizados/cambios_gratuitos_tienda';
        default:
            return undefined;
    }
};

export const getPageTypeForMicrosite = (url: string): PageTypeValue => {
    // Temporal? Evento limited 2024 - ECOMDUTI-26010
    if (
        url.includes('multicamera/360view_women')
        || url.includes('multicamera/360view_men')
        || url.includes('microsite/liveshopping')
        || url.includes('microsite/livestreaming')
    ) {
        return 'parrilla';
    }

    return undefined;
};

export const getCustomPageTypeByPage = (
    page: string,
    url: string,
    product: ProductPublicModels.PublicProduct
): PageTypeValue | undefined => {
    switch (page) {
        case 'ItxAngularCmsRenderPage':
            return getPageTypeForCmsPage(url);

        case 'ItxBagCollectionCreatorsPage':
            return getPageType.ItxBagCollectionCreatorsPage;

        case 'ItxBagCollectionProductsPage':
            return getPageType.ItxBagCollectionProductsPage;

        case 'ItxCategoryPage':
            return getPageTypeForCategoryPage(url);

        case 'ItxProductPage':
            return getPageTypeForProductPage(product?.type);

        case 'ItxCheckoutShippingDeliveryPage':
        case 'ItxCheckoutShippingDeliveryAddressPage':
        case 'ItxCheckoutShippingDeliverySummaryPage':
        case 'ItxCheckoutShippingStorePage':
        case 'ItxCheckoutShippingDropPointPage':
        case 'ItxCheckoutShippingStoreSummaryPage':
        case 'ItxCheckoutShippingDropPointSummaryPage':
            return getPageType.ItxCheckoutShippingPage;

        case 'ItxCollectionPage':
            return getPageTypeForCollectionPage(url);

        case 'ItxMicrositePage':
        case 'ItxMulticameraPage':
            return getPageTypeForMicrosite(url);

        default:
            return getPageType[page] ?? undefined;
    }
};

export const eventFromConsiderEvents: EventFrom[] = [
    EventFrom.search,
    EventFrom.searchAdapter,
    EventFrom.searchNoResults,
    EventFrom.searchAdapterNoResults,
    EventFrom.miniWishList,
    EventFrom.miniCart,
    EventFrom.storeAndLanguageList,
    EventFrom.login,
    EventFrom.loginFacebook,
    EventFrom.retrievePassEmail,
    EventFrom.orderStatusList,
    EventFrom.orderStatusNotFound,
    EventFrom.returnsOrderPage,
    EventFrom.returnsOrderListPage,
    EventFrom.returnsOrderDetailPage,
    EventFrom.returnsOrderConfirmationPage,
    EventFrom.returnsStoreFreeChanges,
    EventFrom.orderStatusDetail,
];

export const getCustomPageType = (customContext: CustomContext, eventFrom: EventFrom): PageTypeValue | undefined => {
    if (isValidModal(customContext.navigationData.modal)) {
        return getCustomPageTypeForModals(
            customContext.navigationData.page,
            customContext.navigationData.modal
        );
    }
    if (isValidSidebarComponent(customContext.sidebarLoadedComponent?.name)) {
        return getCustomPageTypeForSidebars(
            customContext.navigationData.page,
            customContext.sidebarLoadedComponent.name
        );
    }
    if (eventFromConsiderEvents.includes(eventFrom)) {
        return getCustomPageTypeByEventFrom(
            eventFrom
        );
    }
    return getCustomPageTypeByPage(
        customContext.navigationData.page,
        customContext.navigationData.routerState?.url,
        customContext.product,
    );
};

export const getPageTitleForShopcartPage = (isShopcartEmpty: boolean): PageTitleValue => {
    return isShopcartEmpty ? 'vacia' : 'lista_de_productos';
};

export const getPageTitleForWhislistPage = (isWhislistEmpty: boolean): PageTitleValue => {
    return isWhislistEmpty ? 'vacia' : undefined;
};

export const getPageTitleForHomePage = (lastSection: string): PageTitleValue | undefined => {
    switch (lastSection) {
        case ApplicationSections.WOMEN:
            return 'pagina_inicio_women';
        case ApplicationSections.MEN:
            return 'pagina_inicio_men';
        case ApplicationSections.BG:
            return 'pagina_inicio_beauty';
        default:
            return undefined;
    }
};

export const getPageTitleForCategoryPage = (
    categoriesNavigation: MenuCategory[],
    categoryId: string
): string | undefined => {
    return categoriesNavigation.find((category: Category) => category.id === categoryId)?.sectionPathEn;
};

export const getPartNumberWithColor = (product: ProductPublicModels.PublicProduct): string => {
    const splitPartNumber: string[] = product?.reference?.split('-');
    const partNumberWithColor: string = splitPartNumber?.join(`${product.status.selectedColor.id}-`);
    return partNumberWithColor;
};

export const getPageTitleForProductPage = (product: ProductPublicModels.PublicProduct): string => {
    if (product?.type === 'BundleBean') {
        return `${product?.reference}/detalles_bundle`;
    }
    const partNumberWithColor = getPartNumberWithColor(product);
    return `${partNumberWithColor}/detalles_producto`;
};

export const getCategoryFromPath = (categoriesNavigation: MenuCategory[], router: routerPublicModels.MergedRoute) => {
    return categoriesNavigation.find(category => category.locationPath.includes(router.params.path));
};

export const getPageTitleForCollectionCmsPage = (
    router: routerPublicModels.MergedRoute,
    categoriesNavigation: MenuCategory[],
): string | undefined => {
    if (router.params.page === 'stories') {
        const menuCategory = getCategoryFromPath(categoriesNavigation, router);
        const sectionPath = menuCategory?.sectionPathEn.split('/') ?? [];

        if (router.queryParams.vw === 'l') {
            sectionPath.push('lista');
        } else {
            sectionPath.push('grid');
        }
        return sectionPath.join('/');
    }

    return undefined;
};

export const getPageTitleForCmsRenderPage = (router: routerPublicModels.MergedRoute): string | undefined => {
    let lastSegment: string = router.url.split('/').pop();
    lastSegment = lastSegment.split('?')[0];
    // TODO ECOMDUTI-22227 Solicitar todas las páginas de cms
    switch (lastSegment) {
        case 'member-get-member':
            return router.queryParams.sponsorcode;
        case 'intro-member':
            return 'intro_member';
        default:
            return undefined;
    }
};

export const getPageTitleForProductModal = (data: ProductContext): string => {
    if (data.isCart) {
        const title: string = data.relatedType ? 'vista_rapida_producto_cross' : 'vista_rapida_producto';
        return `${title}/${getPartNumberWithColor(data)}/detalles_producto`;
    }
    if (data.bundleReference) {
        return `${data.bundleReference}-${getPartNumberWithColor(data)}/detalles_producto`;
    }
    return `${getPartNumberWithColor(data)}/detalles_producto`;
};

export const getPageTitleforModals = (
    product: ProductPublicModels.PublicProduct,
    modalData: ModalData<any>,
): PageTitleValue | string | undefined => {
    switch (modalData.name) {
        case 'itx-product-stock':
            return `${getPartNumberWithColor(product)}/consulta_stock`;

        case 'product-modal': {
            const data: ProductContext = modalData.data as ProductContext;
            return getPageTitleForProductModal(data);
        }
        case 'app-feel-subscription-modal':
            return 'fidelizacion/condiciones';

        case 'app-my-account-feel-unsuscription-modal':
            return 'baja';

        default:
            return undefined;
    }
};

export const getPageTitleforSidebars = (
    currentProduct: ProductPublicModels.PublicProduct,
    sidebarComponent: SidebarAnalyticsData
) => {
    const targetProduct = sidebarComponent.data?.product || sidebarComponent.data?.baseProduct || currentProduct;

    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (sidebarComponent.name) {
        // El uso de SidebarPublicModels.sidebarCustomComponents provoca errores en los tests
        case 'OutOfStockSidebarComponent':
            return `${getPartNumberWithColor(targetProduct)}/productos_relacionados/sinstock`;

        case 'ProductAddedToCartModalComponent':
            return `${getPartNumberWithColor(targetProduct)}/productos_relacionados/addtocart`;

        case 'ProductComingSoonComponent': {
            const size: ProductPublicModels.PublicProductSize
            | ProductPublicModels.PublicProduct = targetProduct?.status?.selectedSize || targetProduct;
            const title: string = size.visibilityValue === 'COMING_SOON' ? 'coming_soon' : 'back_soon';
            return `${getPartNumberWithColor(targetProduct)}/${title}`;
        }
        case 'OrderReturnRecommendationComponent':
            return 'cambios_gratuitos_tienda';

        case 'SastreRecommenderComponent':
        case 'SizeGuideComponent':
            return 'inicio';

        case 'OrderDetailLayoutComponent':
            return 'lista_sidebar';
    }
};

export const getPageTitleByEventFrom = (
    customContext: CustomContext,
    eventFrom: EventFrom,
): PageTitleValue | string | undefined => {
    switch (eventFrom) {
        case EventFrom.search:
            return `resultados/lista?q=${customContext.navigationData.searchInfo.search_param}`;

        case EventFrom.searchAdapter:
            return `resultados/lista?q=${customContext.navigationData.searchInfo.searchTerm}`;

        case EventFrom.searchNoResults:
            return customContext.navigationData.searchInfo.search_param ? `sin_resultados?q=${customContext.navigationData.searchInfo.search_param}` : 'pagina_inicial';

        case EventFrom.searchAdapterNoResults:
            return customContext.navigationData.searchInfo.searchTerm ? `sin_resultados?q=${customContext.navigationData.searchInfo.searchTerm}` : 'pagina_inicial';

        case EventFrom.miniWishList:
            return getPageTitleForWhislistPage(customContext.shopCartData.isWhislistEmpty);

        case EventFrom.miniCart:
            return getPageTitleForShopcartPage(customContext.shopCartData.isShopcartEmpty);

        case EventFrom.loginFacebook:
            return 'facebook';

        case EventFrom.retrievePassEmail:
            return 'recuperar_contrasena/email';

        case EventFrom.orderDetailsPage:
            return 'detalles_pedido';

        case EventFrom.orderStatusList:
            return 'lista';

        case EventFrom.orderStatusDetail:
            return 'lista_sidebar';

        case EventFrom.orderStatusNotFound:
            return 'sin_resultados';

        case EventFrom.storeAndLanguageList:
            return 'seleccion_tienda_e_idioma_lista';

        case EventFrom.returnsOrderListPage:
            return 'lista_articulos';

        case EventFrom.returnsOrderDetailPage:
            return 'solicitar_recogida';

        case EventFrom.returnsOrderConfirmationPage:
            return 'confirmacion';

        default:
            return undefined;
    }
};

export const getCategoryFromRouterMenuCategories = (
    router: routerPublicModels.MergedRoute,
    categoriesNavigation: MenuCategory[]
): MenuCategory => {
    const celementUrl: string = router.queryParams?.celement;
    const seoIdUrl: string = router.params?.seoId;

    if (celementUrl) {
        return categoriesNavigation.find((cat) => cat.id === celementUrl);
    }
    if (seoIdUrl) {
        return categoriesNavigation.find((cat) => cat.locationPath.includes(seoIdUrl));
    }
    return undefined;
};

export const getPageTitleForMicrosite = (
    lastSection: string,
    router: routerPublicModels.MergedRoute,
    categoriesNavigation: MenuCategory[],
): string => {
    // Temporal? Evento limited 2024 - ECOMDUTI-26010
    if (
        router.url.includes('multicamera/360view_women')
        || router.url.includes('multicamera/360view_men')
        || router.url.includes('microsite/liveshopping')
        || router.url.includes('microsite/livestreaming')
    ) {
        const lastSectionLowercase = lastSection.toLowerCase();
        const sectionLimited = getCategoryFromRouterMenuCategories(router, categoriesNavigation)?.name;
        return `${lastSectionLowercase}/limited/${sectionLimited}`;
    }

    return undefined;
};

export const getPageTitleForMyAccountBenefitsDetail = (url: string) => {
    if (url.includes('otoappointment')) return 'reserva_cita';
    return undefined;
};

export const getTitleFromCategoryShortDescription = (
    categoriesNavigation: MenuCategory[],
    router: routerPublicModels.MergedRoute
) => {
    const menuCategory = getCategoryFromPath(categoriesNavigation, router);
    return menuCategory.shortDescription.replaceAll('-', '_');
};

export const getPageTitleByPage = (
    categoriesNavigation: MenuCategory[],
    page: string,
    lastSection: string,
    category: Category,
    lookbookCategory: MenuCategory,
    shopCartData: Ga4ShopCartDataSelector,
    product: ProductPublicModels.PublicProduct,
    router: routerPublicModels.MergedRoute,
    user: User,
): PageTitleValue | string | undefined => {
    switch (page) {
        case 'ItxWorldWidePage':
            return 'seleccion_tienda_e_idioma';

        case 'ItxHomeBgPage':
        case 'ItxHomeMenPage':
        case 'ItxHomePage':
        case 'ItxHomeWomenPage':
            return getPageTitleForHomePage(lastSection);

        case 'ItxCategoryPage':
            return getPageTitleForCategoryPage(categoriesNavigation, category.id);

        case 'ItxProductPage':
            return getPageTitleForProductPage(product);

        case 'ItxAngularShopcartPage':
            return getPageTitleForShopcartPage(shopCartData.isShopcartEmpty);

        case 'ItxAngularCmsRenderPage':
            return getPageTitleForCmsRenderPage(router);

        case 'ItxAngularStoreLocatorPage':
            return 'lista';

        case 'ItxAngularWishlistPage':
            return getPageTitleForWhislistPage(shopCartData.isWhislistEmpty);

        case 'ItxCheckoutShippingDeliveryPage':
        case 'ItxCheckoutShippingDeliveryAddressPage':
        case 'ItxCheckoutShippingDeliverySummaryPage':
        case 'ItxCheckoutShippingStorePage':
        case 'ItxCheckoutShippingDropPointPage':
        case 'ItxCheckoutShippingStoreSummaryPage':
        case 'ItxCheckoutShippingDropPointSummaryPage':
            return getPageTitleForCheckoutShipping[page];

        case 'ItxCheckoutShippingPage':
        case 'ItxCheckoutPaymentPage':
            return `datos_${getPageType[page]}`;

        case 'ItxCheckoutRepaymentPage':
            return 'datos_pago';

        case 'ItxCheckoutSummaryPage':
            return 'datos_pedido';

        case 'ItxAngularAuthenticationPage':
            return 'login';

        case 'ItxAngularAuthenticationRecoveryPage':
            return 'recuperar_contrasena';

        case 'ItxAngularAuthenticationRegisterPage':
            return 'registro';

        case 'ItxAngularAuthenticationConfirmationPage':
            return 'registro/confirmacion';

        case 'ItxAngularLookbookPage':
            return `${lookbookCategory?.sectionPathEn}/parrilla_looks`;

        case 'ItxAngularGiftCardBalancePage':
            return 'consulta_saldo';

        case 'ItxAngularGiftCardActivationPage':
            return 'activacion';

        case 'ItxAngularOrderStatus':
            return 'localizar_pedido';

        case 'ItxBagCollectionCreatorsPage':
            return 'creadores/lista';

        case 'ItxBagCollectionProductsPage':
            return 'bolsos/lista';

        case 'ItxBagCollectionSliderCreatorsPage':
            return 'creadores/detalle';

        case 'ItxBagCollectionSliderProductsPage':
            return 'bolsos/detalle';

        case 'ItxMicrositePage':
        case 'ItxMulticameraPage':
            return getPageTitleForMicrosite(lastSection, router, categoriesNavigation);

        case 'ItxMyAccountOrdersPage':
            return 'lista';

        case 'ItxMyAccountPage':
            return 'mi_cuenta';

        case 'ItxMyAccountFeelPage':
        case 'ItxMyAccountFeelActivationPage':
            return user.is360 ? 'usuario_feel' : 'usuario_no_feel';

        case 'ItxMyAccountFeelBenefitsPage':
            return 'usuario_feel/beneficios';

        case 'ItxMyAccountFeelHistoryPage':
            return 'usuario_feel/historico';

        case 'ItxMyAccountFeelHowItWorksPage':
            return 'usuario_feel/como_funciona';

        case 'ItxMyAccountFeelBirthdayGiftPage':
            return 'contenido_exclusivo';

        case 'ItxMyAccountFeelStyleAdvisorPage':
            return 'reserva_cita';

        case 'ItxMyAccountFeelGiftingPage':
            return 'detalles_unicos';

        case 'ItxMyAccountFeelPaperlessPage':
            return 'paperless';

        case 'ItxMyAccountFeelPromotionsPage':
            return 'promociones';

        case 'ItxMyAccountFeelCollectionsPage':
            return 'colecciones_especiales';

        case 'ItxMyAccountFeelWhatsappPage':
            return 'whatsapp';

        case 'ItxMyAccountFeelExperiencesPage':
            return 'experiencias_exclusivas';

        case 'ItxMyAccountNewsletterPage':
            return 'newsletter';

        case 'ItxAngularOrderConfirmationPage':
        case 'ItxAngularOrderConfirmationNodePage':
        case 'ItxOrderConfirmationPage':
            return 'datos_pedido';

        case 'ItxMyAccountMenuPage':
            return 'mi_cuenta';

        case 'ItxMyAccountOrderDetailUpdateShippingAddress':
            return 'detalles_pedido';

        case 'ItxMyAccountFeelBenefitsDetailPage':
            return getPageTitleForMyAccountBenefitsDetail(router.url);

        case 'ItxCollectionPage':
            return getPageTitleForCollectionCmsPage(router, categoriesNavigation);

        case 'ItxRunwayPage':
            return getTitleFromCategoryShortDescription(categoriesNavigation, router);

        default:
            return undefined;
    }
};
export const getPageTitle = (
    customContext: CustomContext,
    user: User,
    eventFrom: EventFrom,
): PageTitleValue | string | undefined => {
    if (isValidModal(customContext.navigationData.modal)) {
        return getPageTitleforModals(
            customContext.product,
            customContext.navigationData.modal,
        );
    }
    if (isValidSidebarComponent(customContext.sidebarLoadedComponent?.name)) {
        return getPageTitleforSidebars(
            customContext.product,
            customContext.sidebarLoadedComponent
        );
    }
    if (eventFromConsiderEvents.includes(eventFrom)) {
        return getPageTitleByEventFrom(customContext, eventFrom);
    }
    return getPageTitleByPage(
        customContext.categoryInfo.categoriesNavigation,
        customContext.navigationData.page,
        customContext.lastSection,
        customContext.categoryInfo.category,
        customContext.categoryInfo.lookbookCategory,
        customContext.shopCartData,
        customContext.product,
        customContext.navigationData.routerState,
        user
    );
};

export const getStoreStatus = (isOpenForSale: boolean): StoreStatusValue => {
    return isOpenForSale ? StoreStatusValue.open : StoreStatusValue.close;
};

export const getStep = (page: string): string => {
    switch (page) {
        case 'ItxAngularShopcartPage':
            return '1';
        case 'ItxCheckoutShippingPage':
            return '2';
        case 'ItxCheckoutPaymentPage':
        case 'ItxCheckoutRepaymentPage':
            return '3';
        case 'ItxCheckoutSummaryPage':
            return '4';
        default:
            return undefined;
    }
};

export const getPageSectionByEventFrom = (
    eventFrom: EventFrom
): string | undefined => {
    switch (eventFrom) {
        case EventFrom.miniWishList:
            return pageSections.ItxCategoryPage;

        case EventFrom.miniCart:
            return pageSections.ItxCheckoutPage;

        case EventFrom.search:
        case EventFrom.searchAdapter:
        case EventFrom.searchNoResults:
        case EventFrom.searchAdapterNoResults:
            return pageSections.ItxCategoryPage;

        case EventFrom.storeAndLanguageList:
            return pageSections.ItxWorldWidePage;

        case EventFrom.login:
        case EventFrom.loginFacebook:
            return pageSections.ItxAngularAuthenticationPage;

        case EventFrom.retrievePassEmail:
            return pageSections.ItxMyAccountPage;

        case EventFrom.orderDetailsPage:
        case EventFrom.orderStatusList:
        case EventFrom.orderStatusNotFound:
            return pageSections.ItxAngularOrderStatus;

        case EventFrom.returnsOrderPage:
        case EventFrom.returnsOrderListPage:
        case EventFrom.returnsOrderDetailPage:
        case EventFrom.returnsOrderConfirmationPage:
            return pageSections.ItxMyAccountReturnsPage;

        default:
            return undefined;
    }
};

export const getPageSectionForProductModal = (data: ProductContext): string => {
    if (data.isCart) {
        return pageSections.ItxAngularShopcartPage;
    }
    return pageSections.ItxCategoryPage;
};

export const getPageSectionForModals = (
    modal: ModalData<any>
): string | undefined => {
    switch (modal.name) {
        case 'product-modal':
            return getPageSectionForProductModal(modal.data);

        case 'app-feel-subscription-modal':
        case 'app-my-account-feel-unsuscription-modal':
            return pageSections.ItxMyAccountFeelPage;

        default:
            return pageSections.ItxCategoryPage;
    }
};

export const getPageSectionForSidebars = (
    sidebarComponent: SidebarCustomComponents,
    page: PageSection
): PageSectionValue => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (sidebarComponent) {
        // El uso de SidebarPublicModels.sidebarCustomComponents provoca errores en los tests
        case 'SastreRecommenderComponent':
        case 'SizeGuideComponent':
            return pageSections.ItxHelpPage;

        case 'OrderReturnRecommendationComponent':
        case 'OutOfStockSidebarComponent':
        case 'ProductAddedToCartModalComponent':
        case 'ProductComingSoonComponent':
        case 'OrderDetailLayoutComponent':
            return pageSections[page];
    }
};

export const getPageSectionByCheckoutFlowFlag = (page: PageSection, isCheckoutFlow: boolean): PageSectionValue => {
    switch (page) {
        case 'ItxAngularAuthenticationPage':
        case 'ItxAngularAuthenticationRecoveryPage':
        case 'ItxAngularAuthenticationRegisterPage':
            return isCheckoutFlow ? 'checkout' : 'perfil_usuario';
    }
    return undefined;
};

export const getPageSection = (
    customContext: CustomContext,
    eventFrom: EventFrom
) => {
    if (isValidModal(customContext.navigationData.modal)) {
        return getPageSectionForModals(customContext.navigationData.modal);
    }
    if (isValidSidebarComponent(customContext.sidebarLoadedComponent?.name)) {
        return getPageSectionForSidebars(customContext.sidebarLoadedComponent.name, customContext.navigationData.page);
    }
    if (eventFromConsiderEvents.includes(eventFrom)) {
        return getPageSectionByEventFrom(eventFrom);
    }
    if (PAGES_WITH_PAGE_VIEW_AFFECTED_BY_CHECKOUT_FLOW.includes(customContext.navigationData.page)) {
        return getPageSectionByCheckoutFlowFlag(
            customContext.navigationData.page,
            customContext.navigationData.isCheckoutAuth
        );
    }

    return pageSections[customContext.navigationData.page] ?? 'catalogo';
};

export const getUserLogged = (isLogged: boolean): UserLogguedString => {
    return isLogged ? UserLogguedString.logueado : UserLogguedString.noLogueado;
};

export const getUserProfileStatus = (
    isLogged: boolean,
    isWallet: boolean,
    isShippingWalletValid: boolean
): UserProfileStatus => {
    if (isLogged) {
        if (isShippingWalletValid) {
            return UserProfileStatus.walletShipping;
        }
        return isWallet ? UserProfileStatus.wallet : UserProfileStatus.registered;
    } if (!isLogged) {
        return UserProfileStatus.guest;
    }

    return undefined;
};

export const getFullPageTitle = (customContext: CustomContext, eventFrom: EventFrom, user?: User) => {
    const pageSection: string = getPageSection(customContext, eventFrom);
    const pageType: string = getCustomPageType(customContext, eventFrom);
    const pageTitle: string = getPageTitle(customContext, user, eventFrom);
    return `/${[pageSection, pageType, pageTitle].filter(isDefined).join('/')}`;
};

export const getParamValue = (
    param: GA4ContextParam,
    values: GA4ContextValues,
): string => {
    switch (param) {
        case GA4ContextParam.page_location:
            return values.window?.location?.origin + values.customContext?.navigationData.routerState?.url;

        case GA4ContextParam.page_referrer:
            return getPageReferrer(values.window);

        case GA4ContextParam.page_title:
            return getFullPageTitle(values.customContext, values.eventFrom, values.user);

        case GA4ContextParam.im_page_section:
            return getPageSection(values.customContext, values.eventFrom);

        case GA4ContextParam.im_page_type:
            return getCustomPageType(
                values.customContext,
                values.eventFrom,
            );

        case GA4ContextParam.im_page_title:
            return getPageTitle(
                values.customContext,
                values.user,
                values.eventFrom
            );

        case GA4ContextParam.im_language:
            return values.customContext.context.currentLanguage.locale;

        case GA4ContextParam.im_platform:
            return 'web-responsive';

        case GA4ContextParam.im_local_store_id:
            return getStoreId(values.customContext.context.store.countryCode);

        case GA4ContextParam.im_store_status:
            return getStoreStatus(values.customContext.context.store.isOpenForSale);

        case GA4ContextParam.im_step:
            return getStep(values.customContext.navigationData.page);

        case GA4ContextParam.im_user_email_sha256:
            return getUserEmailSha256(values.user?.email ?? values.window.imAnalytics?.email);

        case GA4ContextParam.user_id:
            return getUserId(values.user?.identity.userId);

        case GA4ContextParam.im_physical_store:
            return undefined;

        case GA4ContextParam.im_product_section:
            return GA4DataUtils.getProductSection(values.customContext.product);

        case GA4ContextParam.im_product_family:
            return GA4DataUtils.getProductFamily(values.customContext.product);

        case GA4ContextParam.im_product_subfamily:
            return GA4DataUtils.getProductSubFamily(values.customContext.product);

        case GA4ContextParam.im_user_logged:
            return getUserLogged(values.user?.isLogged);

        case GA4ContextParam.im_user_loyalty:
            return GA4DataUtils.getBooleanSiNo(values.user?.isVip);

        case GA4ContextParam.im_user_quick_buy:
            return GA4DataUtils.getBooleanSiNo(values.user?.isWalletUser);

        case GA4ContextParam.im_user_profile_status:
            return getUserProfileStatus(
                values.user?.isLogged,
                values.user?.isWalletUser,
                values.user?.isShippingWalletValid
            );

        default:
            return undefined;
    }
};

export const deleteParamFromGroup = (group: GA4ContextGroup, param: GA4ContextParam): GA4ContextGroup => {
    const { [param]: _, ...newGroup } = group;
    return newGroup;
};

export const getParams = (
    config: GA4ContextParam[],
    values: GA4ContextValues,
): GA4ContextGroup => {
    const group: GA4ContextGroup = { ...defaultConfig };
    for (const param of config) {
        group[param] = getParamValue(param, values);
    }

    // Casuística enlace directo => eliminamos parámetro page_referrer del objeto context
    if (group.page_referrer === undefined) {
        return deleteParamFromGroup(group, GA4ContextParam.page_referrer);
    }

    return group;
};

export const ga4ContextProcessConfig = (
    config: GA4ContextParam[],
    values: GA4ContextValues,
): GA4ContextGroup | undefined => {
    if (config?.length) {
        return getParams(config, values);
    }
    return undefined;
};
